import GroupApi from '@api/GroupApi'
import RequestHelper from '@mixins/RequestHelper'
import auth from '@mixins/auth'
import { mapGetters } from 'vuex'
import { errToString } from '@utils'

export default {
  mixins: [
    RequestHelper,
    auth
  ],
  data() {
    return {
      classPlanSubGroups: [],
      requestingNewClassPlans: false
    }
  },
  methods: {
    requestClassPlansForSubGroup(subGroupCk) {
      if (!subGroupCk) {
        this.$set(this, 'classPlanSubGroups', [])
        return
      }

      this.requestingNewClassPlans = true

      new GroupApi({ simulateExample: 'Multiple Class Plans for SubGroup.' }).classPlans({
        subGroupCk: subGroupCk
      })
        .then(res => {
          this.classPlanSubGroups = res.subGroups
        })
        .catch(err => {
          errToString(err)
            .then((errStr) => {
              console.warn(errStr)
            })
        })
        .finally(() => {
          this.requestingNewClassPlans = false
        })
    },
    getClassPlansForSubGroup(subGroupCk) {
      return this.classPlanSubGroups.find(sg => sg.subGroupCK === subGroupCk)
    },
    getFirstClassByClassId(classId) {
      return this.computedClassPlanDictionary[classId]
    },
    getFirstPlanByClassAndPlanId(classId, planId) {
      const classFound = this.computedClassPlanDictionary[classId]
      if (classFound && classFound.plans) {
        return classFound.plans[planId]
      }
      return {}
    },
    plansInClassOptions(classId) {
      if (!this.computedClassPlanDictionary[classId]) {
        return []
      }

      const plans = this.computedClassPlanDictionary[classId].plans
      return Object.keys(plans).map((key, index) => ({
        value: plans[key].planId,
        text: plans[key].planDescription
      }))
    }
  },
  computed: {
    ...mapGetters('groupSelector', ['selected']),
    computedMemberIdSource() {
      try {
        return this.classPlanSubGroups[0].memberIdSource
      } catch (error) {
        return ''
      }
    },
    computedFilteredSubGroups() {
      const subGroupAccess = this.subGroupAccess || []
      if (this.subGroupFilter && typeof (this.subGroupFilter) === 'function') {
        return subGroupAccess.filter(this.subGroupFilter)
      }

      return subGroupAccess
    },
    computedFilteredSubGroupOptions() {
      if (this.computedFilteredSubGroups.length === 0) {
        return []
      }

      let subGroupsInGroup = this.computedFilteredSubGroups.sort((g1, g2) => g1.subGroupName.localeCompare(g2.subGroupName))
      subGroupsInGroup = subGroupsInGroup.map(sg => ({
        value: sg.subGroupCk,
        text: `${sg.subGroupName} - ${sg.subGroupId}`
      }))

      return subGroupsInGroup
    },
    computedClassPlans() {
      let classPlans = []

      this.classPlanSubGroups.forEach(sg => {
        if (!sg.classPlan) {
          return
        }

        sg.classPlan.forEach(cp => {
          classPlans.push({
            ...cp,
            ...{
              subGroupCk: sg.subGroupCk,
              subGroupId: sg.subGroupId,
              subGroupInfo: sg,
              groupTerminationDate: sg.groupTerminationDate,
              groupEffectiveDate: sg.groupEffectiveDate
            }
          })
        })
      })

      return classPlans
    },
    computedClasses() {
      const classes = {}
      this.computedClassPlans.forEach(cp => {
        if (classes.hasOwnProperty(cp.classId)) {
          return
        }

        classes[cp.classId] = {
          classId: cp.classId,
          className: cp.classDescription,
          subGroupId: cp.subGroupId,
          subGroupCk: cp.subGroupCk
        }
      })

      return Object.values(classes)
    },
    computedFilteredClasses() {
      if (this.classFilter && typeof (this.classFilter) === 'function') {
        return this.computedClasses.filter(this.classFilter)
      }

      return this.computedClasses
    },
    computedFilteredClassOptions() {
      return this.computedFilteredClasses.map((c) => ({
        value: c.classId,
        text: `${c.className} - ${c.classId}`
      }))
    },
    computedPlans() {
      const plans = []
      this.computedClassPlans.forEach(cp => {
        if (plans.hasOwnProperty(cp.planId)) {
          return
        }

        plans.push({
          classId: cp.classId,
          planId: cp.planId,
          planName: cp.planDescription,
          subGroupId: cp.subGroupId,
          subGroupCk: cp.subGroupCk,
          subGroupInfo: cp.subGroupInfo
        })
      })

      return plans
    },
    computedFilteredPlans() {
      if (this.planFilter && typeof (this.planFilter) === 'function') {
        return this.computedPlans.filter(this.planFilter)
      }

      return this.computedPlans
    },
    computedFilteredPlanOptions() {
      return this.computedFilteredPlans.map((p) => ({
        value: p.planId,
        text: `${p.planName} - ${p.planId}`
      }))
    },
    computedClassPlanDictionary() {
      const classPlans = {}
      this.computedClassPlans.forEach((c) => {
        if (!classPlans[c.classId]) {
          classPlans[c.classId] = {
            classDescription: c.classDescription,
            classEffectiveDate: c.classEffectiveDate,
            classTerminationDate: c.classTerminationDate,
            classId: c.classId,
            plans: {}
          }
        }

        if (!classPlans[c.classId].plans[c.planId]) {
          classPlans[c.classId].plans[c.planId] = {
            planId: c.planId,
            planDescription: c.planDescription,
            planEffectiveDate: c.planEffectiveDate,
            planTerminationDate: c.planTerminationDate,
            subGroupInfo: c.subGroupInfo
          }
        }
      })

      return classPlans
    },
    computedCurrentSubGroupPlanInfo() {
      if (!this.computedClassPlans || this.computedClassPlans.length === 0) {
        return {}
      }

      let plan = this.computedClassPlans.find(sg => `${sg.subGroupCk}` === `${this.subGroupCk}`)
      if (plan) {
        return plan.subGroupInfo
      }

      return {}
    }
  },
  watch: {
    subgroupCk(newValue) {
      this.subGroupCk = newValue
    },
    'selected' (newGroup, oldGroup) {
      this.subGroupCk = this.selected.subGroupCk
    },
    subGroupCk(newValue) {
      // only happens on the subgroup details page where we don't want the context picker having control
      if (this.contextDependent === false) {} else { this.requestClassPlansForSubGroup(newValue) }
    }
  },

  created() {
    if (this.subGroupCk) {
      this.requestClassPlansForSubGroup(this.subGroupCk)
    }
  }
}
